import {forwardRef} from "react";
import {grey} from "@mui/material/colors";
import {api} from "../../../../api/API";
import {Project, UnitSystem} from "../../../../api/Projects";
import {PurchaseOrder} from "../../../../api/Logistics";
import {CutPlanEnd, CutPlanFace, CutPlanGroup, CutPlanProjectGroup, CutPlanStick} from "../../../../api/ElevationTool";
import {PDFInfo} from "../../../../misc/pdf/PrintManager";
import {useUser} from "../../../../misc/Permission";
import {useReadyCheck} from "../../../../misc/pdf/UseReadyCheck";
import {PDF} from "../../../../misc/pdf/parts/PDF";
import {ContainerPage} from "../../../../misc/pdf/parts/ContainerPage";
import {TitleTextBlock} from "../../../../misc/pdf/parts/TitleTextBlock";
import {pdfTableStyle} from "../../../../misc/pdf/parts/Page";
import {PDFProjectContext} from "../openings/OpeningsPDF";
import {formatDim} from "../elevationTool/components/DimensionEditor";
import {inches} from "../elevationTool/const";

// one screen elevation variant with its drawing resolved to a printable image.
type CutPlanSection = {
    group: CutPlanGroup;
    image: string;
};

// one project's pages: its variants under its own page header.
type CutPlanPart = {
    project: Project;
    sections: CutPlanSection[];
};

async function resolveSections(groups: CutPlanGroup[]): Promise<CutPlanSection[]> {
    return Promise.all(groups.map(async group => ({
        group,
        image: await api.print.fileAsImage({file: group.rendering}),
    })));
}

// thrown by the callers when the openings have no rendered screen elevation to list, so the
// user gets a message instead of an empty PDF.
export const NO_CUT_PLANS_ERROR = "No screen elevations to render: none of these openings has a drawn screen elevation.";

export function getFrameCutPlan(props: {
    project: Project;
    groups: CutPlanGroup[];
}): PDFInfo {
    return {
        name: "frame-cut-plan.pdf",
        render: async (onReady) => {
            const sections = await resolveSections(props.groups);

            return <FrameCutPlanPDF onReady={onReady} parts={[{project: props.project, sections}]} />;
        },
        serverInfo: {},
    };
}

// the frame cut plan of every frame on a purchase order: one part per project the order draws
// from, each with its own variants.
export function getFrameCutPlanForPurchaseOrder(props: {
    purchaseOrder: PurchaseOrder;
    byProject: CutPlanProjectGroup[];
}): PDFInfo {
    return {
        name: `PO${props.purchaseOrder.poNumber}-frame-cut-plan.pdf`,
        render: async (onReady) => {
            const parts = await Promise.all(props.byProject.map(async p => ({
                project: await api.projects.get(p.project),
                sections: await resolveSections(p.groups),
            })));

            return <FrameCutPlanPDF onReady={onReady} parts={parts} />;
        },
        serverInfo: {},
    };
}

const FrameCutPlanPDF = forwardRef(function FrameCutPlanPDF(props: {
    parts: CutPlanPart[];
    onReady(): void;
}, ref: any) {
    const usr = useUser();
    const ready = useReadyCheck(props.onReady);
    ready.require(!!usr);

    return (
        <div ref={ref}>
            <PDF>
                {props.parts.map(part => (
                    <PDFProjectContext.Provider key={part.project.id} value={part.project}>
                        {part.sections.map(s => (
                            <CutPlanPage key={s.group.rendering} section={s} sys={part.project.unitSystem || "imperial"} />
                        ))}
                    </PDFProjectContext.Provider>
                ))}
            </PDF>
        </div>
    );
});

// one variant on its own landscape page: the heading and openings, then the labelled drawing on the
// left and the stick table on the right.
function CutPlanPage(props: {section: CutPlanSection; sys: UnitSystem}) {
    const g = props.section.group;
    const heading = g.label ? `${g.name}-${g.label}` : g.name;
    const first = g.openings[0];

    return (
        <ContainerPage orientation="landscape">
            <TitleTextBlock title="FRAME CUT PLAN" fullWidth>
                <div style={{fontWeight: "bold", fontSize: "1rem"}}>
                    {heading}
                    {first && <span style={{fontWeight: "normal"}}> · {first.nominalWidth} x {first.nominalHeight}</span>}
                </div>
                <div style={{fontSize: "0.7rem", paddingBottom: 8}}>
                    Openings: {g.openings.map(o => `${o.name} (${o.qty})`).join(", ")}
                </div>
                <div style={{flex: 1, display: "flex", flexDirection: "row", minHeight: 0}}>
                    <div
                        data-pdf-image-high-res="true"
                        style={{
                            flex: 2,
                            marginRight: 8,
                            border: "1px solid " + grey["200"],
                            backgroundImage: "url(" + props.section.image + ")",
                            backgroundSize: "contain",
                            backgroundPosition: "center center",
                            backgroundRepeat: "no-repeat",
                        }}
                    />
                    <div style={{flex: 3, marginLeft: 8}}>
                        <StickTable sticks={g.cutPlan?.sticks ?? []} sys={props.sys} />
                    </div>
                </div>
            </TitleTextBlock>
        </ContainerPage>
    );
}

function StickTable(props: {sticks: CutPlanStick[]; sys: UnitSystem}) {
    const fmt = (valueIn: number) => formatDim(valueIn * inches, props.sys);

    return (
        <table className={pdfTableStyle} style={{fontSize: "0.55rem"}}>
            <thead>
                <tr>
                    <th>Label</th>
                    <th>Profile</th>
                    <th>Interface</th>
                    <th>Length</th>
                    <th>Thickness</th>
                    <th>End Contour</th>
                    <th>Notches</th>
                    <th>Detail</th>
                </tr>
            </thead>
            <tbody>
                {props.sticks.map(s => (
                    <tr key={s.label}>
                        <td>{s.label}</td>
                        <td>{profileText(s)}</td>
                        <td>{interfaceText(s)}</td>
                        <td>{lengthText(s, fmt)}</td>
                        <td>{fmt(s.thicknessIn)}</td>
                        <td>{s.ends.map(endText).join(" · ")}</td>
                        <td>{s.notches.length === 0 ? "—" : s.notches.map(n => `${n.label} @ ${fmt(n.positionIn)}`).join(", ")}</td>
                        <td style={{whiteSpace: "pre-line"}}>{detailText(s, fmt) || "—"}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

const sideAbbr: Record<CutPlanFace["side"], string> = {left: "L", right: "R", top: "T", bottom: "B"};
const sideName: Record<CutPlanEnd["side"], string> = {left: "Left", right: "Right", top: "Top", bottom: "Bottom"};

// the faces that touch infill, e.g. "L: STD · R: CO", with "(1-sided)" on a stick that faces infill on
// one side only.
function profileText(s: CutPlanStick): string {
    const faces = s.faces
        .filter(f => f.profiles.length > 0)
        .map(f => `${sideAbbr[f.side]}: ${f.profiles.join(", ")}`)
        .join(" · ");

    return s.sided === "one" ? `${faces} (1-sided)` : faces;
}

// what each face touches, e.g. "L: Door · R: P2 lite". A face against the wall is left out.
function interfaceText(s: CutPlanStick): string {
    return s.faces
        .filter(f => f.interfaces.some(i => i.kind !== "wall"))
        .map(f => `${sideAbbr[f.side]}: ${f.interfaces.map(interfaceName).join(", ")}`)
        .join(" · ");
}

function interfaceName(i: CutPlanFace["interfaces"][number]): string {
    switch(i.kind) {
        case "doors": return "Door";
        case "lite": return i.mark ? `${i.mark} lite` : "Lite";
        case "fixedPanel": return "Fixed panel";
        case "wall": return "Wall";
    }
}

// the cut length and how it adds up, in A-end → B-end order, e.g. 38 1/8" = 1" + 36 1/8" + 1".
// An end that adds nothing is left out; the undercut is subtracted.
function lengthText(s: CutPlanStick, fmt: (valueIn: number) => string): string {
    const [a, b] = s.ends;
    let parts = [
        a && a.addIn > 0 ? fmt(a.addIn) : null,
        fmt(s.nominalIn),
        b && b.addIn > 0 ? fmt(b.addIn) : null,
    ].filter(v => v !== null).join(" + ");

    if(s.undercutIn > 0) parts += ` − ${fmt(s.undercutIn)} UC`;

    // a sloped stick's lengths run along its slope.
    const slope = s.slopeDeg ? ` (sloped ${s.slopeDeg}°)` : "";
    return `${fmt(s.lengthIn)} = ${parts}${slope}`;
}

// the special details on a stick, one line each: an angled mullion's angle and face sizes,
// a hinge jamb's hinge locations (A end of the piece to top of hinge, with the hinge
// height), a strike jamb's strike centres (A end to centre).
function detailText(s: CutPlanStick, fmt: (valueIn: number) => string): string {
    const lines: string[] = [];

    if(s.angled) {
        lines.push(`Angled ${s.angled.deg}° · faces ${fmt(s.angled.insideFaceIn)} inside / ${fmt(s.angled.outsideFaceIn)} outside`);
    }

    if(s.hinges && s.hinges.length > 0) {
        const heights = new Set(s.hinges.map(h => h.heightIn));
        const tops = heights.size === 1
            ? s.hinges.map(h => fmt(h.topIn)).join(", ") + ` (${fmt(s.hinges[0].heightIn)} hinges)`
            : s.hinges.map(h => `${fmt(h.topIn)} (${fmt(h.heightIn)})`).join(", ");
        lines.push(`Hinges, top end to top of hinge: ${tops}`);
    }

    if(s.strikes && s.strikes.length > 0) {
        lines.push(s.strikes.map(k => `${k.kind === "lock" ? "Strike" : "Deadbolt strike"} ctr @ ${fmt(k.centerIn)}`).join(" · "));
    }

    return lines.join("\n");
}

function endText(e: CutPlanEnd): string {
    const contour = e.contour.charAt(0).toUpperCase() + e.contour.slice(1);
    // the cut angle, when the end isn't square (a miter between straight sticks reads 45°).
    const angle = e.angleDeg > 0 ? ` ${e.angleDeg}°` : "";
    return e.otherLabel ? `${sideName[e.side]}: ${contour}${angle} → ${e.otherLabel}` : `${sideName[e.side]}: ${contour}${angle}`;
}
